import { NormalizedProductDeal } from '@/domain/entities/Product';
import { Carousel, SlimSecondaryDealCard } from '@pelando/components';
import { PropsWithChildren } from 'react';
import Link from 'next/link';
import { useTranslation } from '@/presentation/hooks/useTranslation';
import {
  ComparatorSection,
  ContentWrapper,
  HideTextOnDesktop,
  HideTextOnMobile,
  List,
  ListItem,
  NextIndicator,
  PrevIndicator,
} from './styles';

type RelatedProductDealsProps = {
  deals: NormalizedProductDeal[];
  expired?: boolean;
  productUrl: string;
  onCardClick: () => void;
  onCompareButtonClick: () => void;
};

type FilledComparatorSectionProps = PropsWithChildren<
  Pick<RelatedProductDealsProps, 'expired' | 'onCompareButtonClick'>
>;
export function FilledComparatorSection({
  expired,
  onCompareButtonClick,
  children,
}: FilledComparatorSectionProps) {
  const { t } = useTranslation('deal');

  return (
    <ComparatorSection
      title={
        <span>
          {t('comparator-trigger-section-title-1')}
          <HideTextOnMobile>
            {t('comparator-trigger-section-title-2')}
          </HideTextOnMobile>
        </span>
      }
      subtitle={
        expired ? (
          <>
            {t('comparator-trigger-section-expired-subtitle-1')}
            <HideTextOnDesktop>
              {t('comparator-trigger-section-expired-subtitle-2')}
            </HideTextOnDesktop>
          </>
        ) : (
          <>{t('comparator-trigger-section-active-subtitle')}</>
        )
      }
      onCompareButtonClick={onCompareButtonClick}
      showComparareButton
    >
      <ContentWrapper>
        <Carousel.Root>{children}</Carousel.Root>
      </ContentWrapper>
    </ComparatorSection>
  );
}

export function RelatedProductDeals({
  deals,
  expired,
  productUrl,
  onCardClick,
  onCompareButtonClick,
}: RelatedProductDealsProps) {
  const minPrice = Math.min(...deals.map((deal) => deal.price));

  return (
    <FilledComparatorSection
      expired={expired}
      onCompareButtonClick={onCompareButtonClick}
      key={deals.length}
    >
      <PrevIndicator />
      <Link href={productUrl} style={{ display: 'contents' }}>
        <List data-has-few-deals={deals.length <= 2} onClick={onCardClick}>
          {deals.map((deal) => (
            <ListItem key={deal.id}>
              <SlimSecondaryDealCard
                isBestPrice={minPrice === deal.price}
                image={deal.image?.url || ''}
                price={deal.price}
                store={deal.store?.name || ''}
                storeImage={deal.store?.image.url || ''}
                title={deal.title}
              />
            </ListItem>
          ))}
        </List>
      </Link>
      <NextIndicator />
    </FilledComparatorSection>
  );
}
